import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SiteCustomizationContext = createContext(null);

export function SiteCustomizationProvider({ children }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/site-customization/public`);
      setConfig(res.data);
      applyThemeColors(res.data.theme_colors);
    } catch (err) {
      console.error("Failed to fetch site customization:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Listen for live preview messages from admin panel iframe parent
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === "SITE_CUSTOMIZATION_PREVIEW") {
        const payload = event.data.payload;
        setConfig(payload);
        applyThemeColors(payload.theme_colors);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const isSectionEnabled = useCallback(
    (sectionKey) => {
      if (!config?.section_config) return true;
      const sec = config.section_config[sectionKey];
      return sec ? sec.enabled !== false : true;
    },
    [config]
  );

  const getContent = useCallback(
    (blockKey, fallback = "") => {
      if (!config?.content_blocks) return fallback;
      return config.content_blocks[blockKey] ?? fallback;
    },
    [config]
  );

  const getSectionOverride = useCallback(
    (sectionKey, colorKey) => {
      if (!config?.theme_colors?.overrides?.[sectionKey]) return null;
      return config.theme_colors.overrides[sectionKey][colorKey] ?? null;
    },
    [config]
  );

  return (
    <SiteCustomizationContext.Provider
      value={{ config, loading, isSectionEnabled, getContent, getSectionOverride, refetch: fetchConfig }}
    >
      {children}
    </SiteCustomizationContext.Provider>
  );
}

export function useSiteCustomization() {
  const ctx = useContext(SiteCustomizationContext);
  if (!ctx) {
    return {
      config: null,
      loading: true,
      isSectionEnabled: () => true,
      getContent: (_, fallback = "") => fallback,
      getSectionOverride: () => null,
      refetch: () => {},
    };
  }
  return ctx;
}

function applyThemeColors(themeColors) {
  if (!themeColors?.global) return;
  const root = document.documentElement;
  const g = themeColors.global;
  root.style.setProperty("--color-site-primary", g.primary || "#0f172a");
  root.style.setProperty("--color-site-secondary", g.secondary || "#64748b");
  root.style.setProperty("--color-site-accent", g.accent || "#e0482d");
  root.style.setProperty("--color-site-background", g.background || "#ffffff");
  root.style.setProperty("--color-site-surface", g.surface || "#f8fafc");
  root.style.setProperty("--color-site-text-primary", g.text_primary || "#0f172a");
  root.style.setProperty("--color-site-text-secondary", g.text_secondary || "#64748b");
  root.style.setProperty("--color-site-border", g.border || "#e2e8f0");
  root.style.setProperty("--color-site-button-bg", g.button_bg || "#e0482d");
  root.style.setProperty("--color-site-button-text", g.button_text || "#ffffff");
  root.style.setProperty("--color-site-header-bg", g.header_bg || "#0f172a");
  root.style.setProperty("--color-site-header-text", g.header_text || "#ffffff");
  root.style.setProperty("--color-site-footer-bg", g.footer_bg || "#0f172a");
  root.style.setProperty("--color-site-footer-text", g.footer_text || "#ffffff");
  root.style.setProperty("--color-site-link", g.link || "#e0482d");
}
